/* eslint-disable react/jsx-fragments */
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Grid from '@material-ui/core/Grid'
import {
  getContext,
  scrollTo
} from './services'
import DefaultErrorMessage from '../../../common/components/ErrorBoundary/errorMessage'
import APILoadingSpinnerRenderer from '../../../app/components/apiLoadingSpinner'
import {
  CustomH5,
  CustomPaper,
  CustomTitle,
  JurisdictionType,
  StickyTopicNav,
  TopicNav,
  WageAmount,
  WageAmountLabel,
  WageDetailList
} from './styles'
import { formatCurrency } from '../../../common/helpers'
import { StyledExternalLink, H3GovDocsRed } from '../../../theme/Global'
import { Modal } from '../../../common/components/modal'
import IncreaseSchedule from '../increaseSchedule'
import LocationAddress from '../../../common/components/locationAddress'
import queryJurisdictionWithMultipleTippedWages from '../statelessLocationTable/service/queryJurisdictionsWithMultipleTippedWages'
import wageService from '../../../services/wageService'
import jurisdictionService from '../../../services/jurisdictionService'
import locationService from '../../../services/locationService'
import StandardWage from '../standardWage'
import TippedWage from '../tippedWage'
import TopicItem from '../topicItem'

const DetailComponent = (props) => {
  const [location, setLocation] = useState([])
  const [jurisdiction, setJurisdiction] = useState(null)
  const [jurisdictionType, setJurisdictionType] = useState(null)
  const [jurisdictionList, setJurisdictionList] = useState([])
  const [currentTopic, setCurrentTopic] = useState(null)
  const [isTipped, setIsTipped] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [jurisdictionTopics, setJurisdictionTopics] = useState(false)
  const [error, setError] = useState(false)

  const performScroll = (key) => {
    if (scrollTo(key)) {
      setCurrentTopic(key)
    }
  }

  const showIncreaseSchedule = () => {
    setModalOpen(true)
  }

  const getJurisdictionsWithMultipleTippedWages = async () => {
    let jurisdictionsWithMultipleTippedWages = {}

    try {
      const returnedData = await Promise.all([
        queryJurisdictionWithMultipleTippedWages()
      ])

      jurisdictionsWithMultipleTippedWages = returnedData[0].map((jd) => jd.uniqueName)
    } catch (err) {
      console.error(err)
      setError(err)
    }

    return jurisdictionsWithMultipleTippedWages
  }

  const buildContext = (locationInformation, newJurisdiction) => {
    try {
      setLocation(locationInformation)
      const newContext = getContext(locationInformation, newJurisdiction)
      setJurisdictionTopics(newContext.jurisdictionTopics)
      setJurisdiction(newContext.jurisdiction)
      setJurisdictionType(newContext.jurisdictionType)
      setJurisdictionList(newContext.jurisdictionList)
    } catch (err) {
      console.error(err)
      setError(err)
    }
  }

  const buildRelatedJurisdictions = (locationResponse) => {
    const buildLegacyJd = (hydratedJurisdiction = {}) => {
      return {
        standard: {
          jurisdiction: {
            name: hydratedJurisdiction.name,
            type: hydratedJurisdiction.type
          },
          wage: hydratedJurisdiction.products.minimumWage.applicableWages.standard
        },
        tipped: {
          jurisdiction: {
            name: hydratedJurisdiction.name,
            type: hydratedJurisdiction.type
          },
          wage: hydratedJurisdiction.products.minimumWage.applicableWages.tipped
        },
        topics: hydratedJurisdiction.products.minimumWage.topics
      }
    }

    // because override jurisdictions and groups - this keeps e.g. an empty city that overrides to county from displaying
    const jdHasTypeAndContent = (jd, type) => {
      return jd.type === type
        && ( jd.products.minimumWage.topics.length
          || jd.products.minimumWage.applicableWages.standard
          || jd.products.minimumWage.applicableWages.tipped
        )
    }

    const hydratedCityJurisdiction = locationResponse.jurisdictions.find((jd) => jdHasTypeAndContent(jd, 'city'))
    const hydratedCountyJurisdiction = locationResponse.jurisdictions.find((jd) => jdHasTypeAndContent(jd, 'county'))
    const hydratedStateJurisdiction = locationResponse.jurisdictions.find((jd) => jdHasTypeAndContent(jd, 'state'))
    const hydratedFederalJurisdiction = locationResponse.jurisdictions.find((jd) => jdHasTypeAndContent(jd, 'federal'))

    const result = {}

    if (hydratedCityJurisdiction) result.city = buildLegacyJd(hydratedCityJurisdiction)
    if (hydratedCountyJurisdiction) result.county = buildLegacyJd(hydratedCountyJurisdiction)
    if (hydratedStateJurisdiction) result.state = buildLegacyJd(hydratedStateJurisdiction)
    if (hydratedFederalJurisdiction) result.federal = buildLegacyJd(hydratedFederalJurisdiction)

    return result
  }

  const buildLocationWages = (wageSchedules, companyDoc, jdUniqueNameToDocDict) => {
    const relevantWageSchedule = wageService.getIndustryApplicableWageSchedule(wageSchedules, companyDoc)
    const now = new Date()
    const standardWage = wageService.getEffectiveWageForDate(relevantWageSchedule.standardSchedule, now)
    const tippedWage = wageService.getEffectiveWageForDate(relevantWageSchedule.tippedSchedule, now)

    return {
      standard: {
        wage: standardWage.rate,
        jurisdiction: { type: jdUniqueNameToDocDict[standardWage.jurisdictionUniqueName] && jdUniqueNameToDocDict[standardWage.jurisdictionUniqueName].type }
      },
      tipped: {
        wage: tippedWage.rate,
        jurisdiction: { type: jdUniqueNameToDocDict[tippedWage.jurisdictionUniqueName] && jdUniqueNameToDocDict[tippedWage.jurisdictionUniqueName].type }
      }
    }
  }

  const buildIncreaseSchedule = (wageSchedules, companyDoc, jdUniqueNameToDocDict) => {
    const relevantWageSchedule = wageService.getIndustryApplicableWageSchedule(wageSchedules, companyDoc)

    const now = new Date()
    const filterToFutureWages = (wageDoc) => (new Date(wageDoc.effectiveDate) > now)

    const toIncreaseSchedule = (wageDoc) => ({
      jurisdiction: {
        name: jdUniqueNameToDocDict[wageDoc.jurisdictionUniqueName].name,
        type: jdUniqueNameToDocDict[wageDoc.jurisdictionUniqueName].type
      },
      effectiveDate: wageDoc.effectiveDate,
      wage: wageDoc.rate
    })

    return {
      standard: relevantWageSchedule.standardSchedule.filter(filterToFutureWages).map(toIncreaseSchedule),
      tipped: relevantWageSchedule.tippedSchedule.filter(filterToFutureWages).map(toIncreaseSchedule)
    }
  }

  const mapLocationInformation = async (locationResponse, locationId, sessionCompany) => {
    const jdsWithMultipleTippedWages = await getJurisdictionsWithMultipleTippedWages()
    const locationInformation = locationResponse

    const jdUniqueNameToDocDict = await jurisdictionService.getJdUniqueNameToDocDict(
      locationResponse.jurisdictions.map((jd) => jd.uniqueName)
    )

    locationInformation.hasMultipleTippedWages = locationResponse.jurisdictions && locationResponse.jurisdictions.some((jd) => jdsWithMultipleTippedWages.includes(jd.uniqueName))
    locationInformation.increaseSchedule = buildIncreaseSchedule(locationResponse.wageSchedules, sessionCompany, jdUniqueNameToDocDict)
    locationInformation.wages = buildLocationWages(locationResponse.wageSchedules, sessionCompany, jdUniqueNameToDocDict)
    locationInformation.relatedJurisdictions = buildRelatedJurisdictions(locationResponse)
    buildContext(locationInformation, jurisdiction)
    setLoading(false)
  }


  const loadLocationDetails = async (sessionCompany, locationId) => {
    const locationResponse = await locationService.getLocationDetails({ mongoId: locationId })
    const locationInformation = await mapLocationInformation(locationResponse.body, locationId, sessionCompany)

    // todo: something sane instead of the mad spaghetti of buildContext
    //   but probably not until after ML is fully removed and we probably rebuild this whole page

    if (locationInformation && jurisdiction) {
      buildContext(locationInformation, jurisdiction)
      setLoading(false)
    }
  }

  const company = useSelector((state) => {
    return state.account.company
  })

  useEffect(() => {
    let subscribed = true
    if (subscribed) {
      const { id } = props.match.params
      setIsTipped(company.hasTippedEmployees)
      setLoading(true)
      loadLocationDetails(company, id).catch((getLocationDetailsError) => {
        setError(getLocationDetailsError.message || 'Unexpected error')
        setLoading(false)
      })
    }

    return () => subscribed = false
  }, [])

  if (error) {
    return <DefaultErrorMessage error={error} data-testid="error-container" />
  }

  return loading ? (
    <APILoadingSpinnerRenderer withoutPaper />
  ) : (
    <React.Fragment>
      {jurisdiction.standard ? (
        <CustomTitle>
          {`${location.displayName} - ${jurisdiction.standard.jurisdiction.name}`}
        </CustomTitle>
      ) : (
        <CustomTitle>
          {`${location.displayName} - ${jurisdiction.tipped.jurisdiction.name}`}
        </CustomTitle>
      )}
      <Grid container spacing={24}>
        <Grid item xs={9} key={0}>
          <CustomPaper>
            <Grid container spacing={24}>
              <Grid item xs={6}>
                <Grid
                  container
                  spacing={8}
                  direction="row"
                  justify="flex-start"
                  alignItems="center"
                >
                  <Grid item xs={4}>
                    <WageAmount>{formatCurrency(location.wages.standard.wage)}</WageAmount>
                  </Grid>
                  <Grid item xs>
                    <WageAmountLabel>Minimum Wage</WageAmountLabel>
                    <JurisdictionType>{location.wages.standard.jurisdiction.type}</JurisdictionType>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Grid
                  container
                  spacing={8}
                  direction="row"
                  justify="flex-start"
                  alignItems="center"
                >
                  {location.hasMultipleTippedWages ? (
                    <Grid item xs>
                      <WageAmountLabel>
                        <StyledExternalLink onClick={() => {
                          performScroll('Gratuities')
                        }}
                        >
                          View Gratuities Section
                        </StyledExternalLink>
                      </WageAmountLabel>
                    </Grid>
                  ) : (
                    <React.Fragment>
                      {isTipped && location.wages.tipped && location.wages.tipped.wage ? (
                        <React.Fragment>
                          <Grid item xs={4}>
                            <WageAmount>{formatCurrency(location.wages.tipped.wage)}</WageAmount>
                          </Grid>
                          <Grid item xs>
                            <WageAmountLabel>Tipped Wage</WageAmountLabel>
                            <JurisdictionType>{location.wages.tipped.jurisdiction.type}</JurisdictionType>
                          </Grid>
                        </React.Fragment>
                      ) : null}
                      {isTipped && location.wages.tipped && !location.wages.tipped.wage ? (
                        <Grid item xs={12}>
                          {location.wages.tipped.context}
                          <br />
                          {location.wages.tipped.jurisdiction.type}
                        </Grid>
                      ) : null}
                    </React.Fragment>
                  ) }

                </Grid>
              </Grid>

              <LocationAddress location={location} />
              <Grid item xs={12}>
                <CustomH5>Applicable Jurisdictions</CustomH5>
                <Grid container spacing={24}>
                  {jurisdictionList.map((jurisdictionItem) => (
                    <Grid item xs={6}>
                      <StyledExternalLink
                        tabIndex={0}
                        isActive={jurisdictionType === jurisdictionItem.key}
                        data-active={jurisdictionType === jurisdictionItem.key}
                        role="button"
                        onClick={() => {
                          buildContext(location, location.relatedJurisdictions[jurisdictionItem.key])
                        }}
                        onKeyDown={() => {
                          buildContext(location, location.relatedJurisdictions[jurisdictionItem.key])
                        }}
                        key={jurisdictionItem.key}
                      >
                        {jurisdictionItem.name}
                      </StyledExternalLink>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {jurisdiction.standard ? (
                  <H3GovDocsRed>
                    {`${jurisdiction.standard.jurisdiction.name} Wage Details`}
                  </H3GovDocsRed>
                ) : (
                  <H3GovDocsRed>
                    {`${jurisdiction.tipped.jurisdiction.name} Wage Details`}
                  </H3GovDocsRed>
                )}
                <Grid
                  container
                  spacing={8}
                  direction="row"
                  justify="flex-start"
                  alignItems="center"
                >

                  <StandardWage jurisdiction={jurisdiction} jurisdictionType={jurisdictionType} />

                  <TippedWage
                    isTipped={isTipped}
                    jurisdiction={jurisdiction}
                    jurisdictionType={jurisdictionType}
                    hasMultipleTippedWages={location.hasMultipleTippedWages}
                    performScroll={performScroll}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <WageDetailList>
                  {Object.keys(jurisdictionTopics)
                    .map((topic) => {
                      if (!isTipped && topic === 'Gratuities') {
                        return null
                      }
                      const topicItem = jurisdictionTopics[topic]
                      return (
                        <TopicItem
                          performScroll={performScroll}
                          topicItem={topicItem}
                          jurisdictionType={jurisdictionType}
                          stateCode={location.address.stateCode}
                          topic={topic}
                        />
                      )
                    })}
                </WageDetailList>
              </Grid>
            </Grid>
          </CustomPaper>
        </Grid>

        <Grid item xs={3} key={1}>
          <StickyTopicNav>
            {Object.keys(jurisdictionTopics)
              .map((topic) => {
                if (!isTipped && topic === 'Gratuities') {
                  return null
                }
                return (
                  <li key={topic}>
                    <TopicNav
                      className={currentTopic === topic ? 'selected' : null}
                      role="button"
                      tabIndex={0}
                      onKeyDown={() => {
                        performScroll(topic)
                      }}
                      onClick={() => {
                        performScroll(topic)
                      }}
                    >
                      {topic}
                    </TopicNav>
                  </li>
                )
              })}
            {Object.keys(location.increaseSchedule).length > 0 && location.increaseSchedule.standard !== undefined ? (
              <li>
                <div
                  style={{
                    cursor: 'pointer',
                    paddingLeft: '18px'
                  }}
                  tabIndex={0}
                  role="button"
                  onKeyDown={showIncreaseSchedule}
                  onClick={showIncreaseSchedule}
                >
                  View Increase Schedule
                </div>
              </li>
            ) : null}
          </StickyTopicNav>
        </Grid>
      </Grid>
      <Modal
        open={modalOpen}
        title="Minimum Wage Increases"
        component={<IncreaseSchedule data={location.increaseSchedule.standard} />}
        modalCloseDispatch={() => {
          setModalOpen(false)
        }}
        isFullWidth
        maxWidth="md"
      />
    </React.Fragment>
  )
}

export default DetailComponent
